import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { Character } from '../../store/slices/charactersSlice';

interface BulkDeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  characters: Character[];
  isDeleting: boolean;
}

const BulkDeleteConfirmationDialog: React.FC<BulkDeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  characters,
  isDeleting,
}) => {
  const [step, setStep] = useState(1);
  const [showCharacterList, setShowCharacterList] = useState(false);
  const [confirmationChecks, setConfirmationChecks] = useState({
    understand: false,
    permanent: false,
    final: false,
  });

  const handleClose = () => {
    if (!isDeleting) {
      setStep(1);
      setConfirmationChecks({ understand: false, permanent: false, final: false });
      setShowCharacterList(false);
      onClose();
    }
  };

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setStep(1);
      setConfirmationChecks({ understand: false, permanent: false, final: false });
      setShowCharacterList(false);
    }
  }, [open]);

  const handleNextStep = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleConfirmationCheck = (key: keyof typeof confirmationChecks) => {
    setConfirmationChecks(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const canProceedToStep2 = step === 1 && characters.length > 0;
  const canProceedToStep3 = step === 2 && confirmationChecks.understand && confirmationChecks.permanent && characters.length > 0;
  const canConfirm = step === 3 && confirmationChecks.final && characters.length > 0;

  const renderStep1 = () => (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <WarningIcon color='error' sx={{ fontSize: 40 }} />
          <Typography variant='h6' color='error'>
            Delete All Characters
          </Typography>
        </Box>

        <Alert severity='warning' sx={{ mb: 3 }}>
          You are about to delete <strong>{characters.length}</strong> character{characters.length !== 1 ? 's' : ''} from your current
          filtered view.
        </Alert>

        <Typography variant='body1' sx={{ mb: 2 }}>
          This action will permanently delete the following characters:
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Button
            onClick={() => setShowCharacterList(!showCharacterList)}
            endIcon={showCharacterList ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            variant='outlined'
            size='small'
          >
            {showCharacterList ? 'Hide' : 'Show'} Character List ({characters.length})
          </Button>
        </Box>

        <Collapse in={showCharacterList}>
          <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <List dense>
              {characters.map(character => (
                <ListItem key={character.id} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={character.name}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={character.game} size='small' variant='outlined' />
                        <Chip label={character.league} size='small' variant='outlined' />
                        <Chip label={`Level ${character.level}`} size='small' variant='outlined' />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>

        <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
          This action cannot be undone. All character data, including item history and events, will be permanently removed.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button onClick={handleNextStep} variant='contained' color='warning' disabled={!canProceedToStep2 || isDeleting}>
          Continue
        </Button>
      </DialogActions>
    </>
  );

  const renderStep2 = () => (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <WarningIcon color='error' sx={{ fontSize: 40 }} />
          <Typography variant='h6' color='error'>
            Confirmation Required
          </Typography>
        </Box>

        <Alert severity='error' sx={{ mb: 3 }}>
          <strong>DANGER:</strong> This action is irreversible and will permanently delete {characters.length} character
          {characters.length !== 1 ? 's' : ''}.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox checked={confirmationChecks.understand} onChange={() => handleConfirmationCheck('understand')} color='error' />
            }
            label='I understand that this action will permanently delete all selected characters'
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox checked={confirmationChecks.permanent} onChange={() => handleConfirmationCheck('permanent')} color='error' />
            }
            label='I understand that this action cannot be undone and all character data will be lost'
          />
        </Box>

        <Typography variant='body2' color='text.secondary'>
          Please confirm both statements above to proceed to the final confirmation.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handlePreviousStep} disabled={isDeleting}>
          Back
        </Button>
        <Button onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button onClick={handleNextStep} variant='contained' color='error' disabled={!canProceedToStep3 || isDeleting}>
          Continue to Final Confirmation
        </Button>
      </DialogActions>
    </>
  );

  const renderStep3 = () => (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <DeleteIcon color='error' sx={{ fontSize: 40 }} />
          <Typography variant='h6' color='error'>
            Final Confirmation
          </Typography>
        </Box>

        <Alert severity='error' sx={{ mb: 3 }}>
          <strong>LAST CHANCE:</strong> You are about to permanently delete {characters.length} character
          {characters.length !== 1 ? 's' : ''}.
        </Alert>

        <Typography variant='body1' sx={{ mb: 2, fontWeight: 'bold' }}>
          Are you absolutely sure you want to proceed?
        </Typography>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={confirmationChecks.final} onChange={() => handleConfirmationCheck('final')} color='error' />}
            label={`Yes, I want to permanently delete ${characters.length} character${characters.length !== 1 ? 's' : ''}`}
          />
        </Box>

        <Typography variant='body2' color='text.secondary'>
          This is your final chance to cancel this operation.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handlePreviousStep} disabled={isDeleting}>
          Back
        </Button>
        <Button onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          color='error'
          disabled={!canConfirm || isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {isDeleting ? 'Deleting...' : `Delete ${characters.length} Character${characters.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </>
  );

  // Don't render dialog if no characters
  if (characters.length === 0 && open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth aria-labelledby='bulk-delete-dialog-title'>
      <DialogTitle id='bulk-delete-dialog-title'>Step {step} of 3: Delete All Characters</DialogTitle>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </Dialog>
  );
};

export default BulkDeleteConfirmationDialog;
